import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Role } from '@/common/model/types';
import { AppRoutes } from './app_routes';
import { RouteFallback } from '@/components/RouteFallback';

import { ProtectedRoute } from '@/features/auth/widget/ProtectedRoute';
import { PlayerAccessRoute } from '@/features/team/widget/PlayerAccessRoute';
import { PageTitle } from './PageTitle';
import { isNativePlatform } from '@/lib/platform';
import { NativeExternalPage } from '@/features/native-app/widget/NativeExternalPage';
import { AppRouteFallback } from '@/features/native-app/widget/AppRouteFallback';

const LoginScreen = lazy(() =>
  import('@/features/auth/screen/LoginScreen').then((m) => ({ default: m.LoginScreen })),
);
const RegisterScreen = lazy(() =>
  import('@/features/auth/screen/RegisterScreen').then((m) => ({ default: m.RegisterScreen })),
);
const JoinScreen = lazy(() =>
  import('@/features/auth/screen/JoinScreen').then((m) => ({ default: m.JoinScreen })),
);
const ForgotPasswordScreen = lazy(() =>
  import('@/features/auth/screen/ForgotPasswordScreen').then((m) => ({ default: m.ForgotPasswordScreen })),
);
const ResetPasswordScreen = lazy(() =>
  import('@/features/auth/screen/ResetPasswordScreen').then((m) => ({ default: m.ResetPasswordScreen })),
);
const ConfirmEmailScreen = lazy(() =>
  import('@/features/auth/screen/ConfirmEmailScreen').then((m) => ({ default: m.ConfirmEmailScreen })),
);
const PendingEmailScreen = lazy(() =>
  import('@/features/auth/screen/PendingEmailScreen').then((m) => ({ default: m.PendingEmailScreen })),
);

const LandingScreen = lazy(() =>
  import('@/features/dashboard/screen/LandingScreen').then((m) => ({ default: m.LandingScreen })),
);
const PricingScreen = lazy(() =>
  import('@/features/pricing/screen/PricingScreen').then((m) => ({ default: m.PricingScreen })),
);
const DemoScreen = lazy(() =>
  import('@/features/dashboard/screen/DemoScreen').then((m) => ({ default: m.DemoScreen })),
);
const DataVolleyAlternativeScreen = lazy(() =>
  import('@/features/comparison/screen/DataVolleyAlternativeScreen').then((m) => ({
    default: m.DataVolleyAlternativeScreen,
  })),
);
const DataVolleyVsScreen = lazy(() =>
  import('@/features/comparison/screen/DataVolleyVsScreen').then((m) => ({
    default: m.DataVolleyVsScreen,
  })),
);
const PaymentSuccessScreen = lazy(() =>
  import('@/features/pricing/screen/PaymentSuccessScreen').then((m) => ({
    default: m.PaymentSuccessScreen,
  })),
);
const HomeScreen = lazy(() =>
  import('@/features/dashboard/screen/HomeScreen').then((m) => ({ default: m.HomeScreen })),
);

const MatchSetupScreen = lazy(() =>
  import('@/features/match/screen/MatchSetupScreen').then((m) => ({ default: m.MatchSetupScreen })),
);
const MatchImportScreen = lazy(() =>
  import('@/features/match/screen/MatchImportScreen').then((m) => ({ default: m.MatchImportScreen })),
);
const DvwUploadScreen = lazy(() =>
  import('@/features/match-import/screens/DvwUploadScreen').then((m) => ({ default: m.DvwUploadScreen })),
);
const DvwImportDraftScreen = lazy(() =>
  import('@/features/match-import/screens/DvwImportDraftScreen').then((m) => ({ default: m.DvwImportDraftScreen })),
);
const MatchInputScreen = lazy(() =>
  import('@/features/match/screen/MatchInputScreen').then((m) => ({ default: m.MatchInputScreen })),
);
const AllMatchesScreen = lazy(() =>
  import('@/features/match/screen/AllMatchesScreen').then((m) => ({ default: m.AllMatchesScreen })),
);

const TeamListScreen = lazy(() =>
  import('@/features/team/screen/TeamListScreen').then((m) => ({ default: m.TeamListScreen })),
);
const TeamSetupScreen = lazy(() =>
  import('@/features/team/screen/TeamSetupScreen').then((m) => ({ default: m.TeamSetupScreen })),
);
const GamePlansScreen = lazy(() =>
  import('@/features/scouting/screen/GamePlansScreen').then((m) => ({ default: m.GamePlansScreen })),
);
const GamePlanViewScreen = lazy(() =>
  import('@/features/scouting/screen/GamePlanViewScreen').then((m) => ({ default: m.GamePlanViewScreen })),
);
const OpponentTeamDetailScreen = lazy(() =>
  import('@/features/scouting/screen/OpponentTeamDetailScreen').then((m) => ({
    default: m.OpponentTeamDetailScreen,
  })),
);

const ProfileScreen = lazy(() =>
  import('@/features/profile/screen/ProfileScreen').then((m) => ({ default: m.ProfileScreen })),
);

const StatsScreen = lazy(() =>
  import('@/features/stats/screen/StatsScreen').then((m) => ({ default: m.StatsScreen })),
);

const MembershipRequestsScreen = lazy(() =>
  import('@/features/membership-request/screen/MembershipRequestsScreen').then((m) => ({
    default: m.MembershipRequestsScreen,
  })),
);

const CartographyScreen = lazy(() =>
  import('@/features/cartography/screen/CartographyScreen').then((m) => ({ default: m.CartographyScreen })),
);

const MatchReviewScreen = lazy(() =>
  import('@/features/match-review/screen/MatchReviewScreen').then((m) => ({ default: m.MatchReviewScreen })),
);

const VideoScreen = lazy(() =>
  import('@/features/video/screen/VideoScreen').then((m) => ({ default: m.VideoScreen })),
);

const SuperAdminScreen = lazy(() =>
  import('@/features/super_admin/screen/SuperAdminScreen').then((m) => ({ default: m.SuperAdminScreen })),
);

const FaqScreen = lazy(() =>
  import('@/features/faq/screen/FaqScreen').then((m) => ({ default: m.FaqScreen })),
);
const TutorialsScreen = lazy(() =>
  import('@/features/tutorials/screen/TutorialsScreen').then((m) => ({ default: m.TutorialsScreen })),
);
const TutorialArticleScreen = lazy(() =>
  import('@/features/tutorials/screen/TutorialArticleScreen').then((m) => ({ default: m.TutorialArticleScreen })),
);

const MentionsLegalesScreen = lazy(() =>
  import('@/features/legal/screen/MentionsLegalesScreen').then((m) => ({ default: m.MentionsLegalesScreen })),
);
const ConfidentialiteScreen = lazy(() =>
  import('@/features/legal/screen/ConfidentialiteScreen').then((m) => ({ default: m.ConfidentialiteScreen })),
);
const CguScreen = lazy(() =>
  import('@/features/legal/screen/CguScreen').then((m) => ({ default: m.CguScreen })),
);
const CgvScreen = lazy(() =>
  import('@/features/legal/screen/CgvScreen').then((m) => ({ default: m.CgvScreen })),
);

const NativeSubscriptionScreen = lazy(() =>
  import('@/features/native-app/screen/NativeSubscriptionScreen').then((m) => ({
    default: m.NativeSubscriptionScreen,
  })),
);
const NativeMoreScreen = lazy(() =>
  import('@/features/native-app/screen/NativeMoreScreen').then((m) => ({ default: m.NativeMoreScreen })),
);

export { RouteFallback };

export const AppPages: React.FC = () => {
  // The app opens on the signed-in home (or sign-in), never on marketing pages,
  // and sells nothing itself: store rules forbid a Stripe payment in the app.
  const native = isNativePlatform();
  const toHome = <Navigate to={AppRoutes.dashboard.home} replace />;
  const legal = (path: string, screen: React.ReactNode) => (native ? <NativeExternalPage path={path} /> : screen);

  return (
    <Suspense fallback={<AppRouteFallback />}>
      <Routes>
        <Route path={AppRoutes.auth.login} element={<LoginScreen />} />
        <Route path={AppRoutes.auth.register} element={
          <PageTitle titleKey="register"><RegisterScreen /></PageTitle>
        } />
        <Route path={AppRoutes.auth.join} element={
          <PageTitle titleKey="join"><JoinScreen /></PageTitle>
        } />
        <Route path={AppRoutes.auth.forgotPassword} element={
          <PageTitle titleKey="forgot_password"><ForgotPasswordScreen /></PageTitle>
        } />
        <Route path={AppRoutes.auth.resetPassword} element={
          <PageTitle titleKey="reset_password"><ResetPasswordScreen /></PageTitle>
        } />
        <Route path={AppRoutes.auth.pendingEmail} element={
          <PageTitle titleKey="pending_email"><PendingEmailScreen /></PageTitle>
        } />
        <Route path={AppRoutes.dashboard.verifyEmail} element={
          <PageTitle titleKey="verify_email"><ConfirmEmailScreen /></PageTitle>
        } />

        {/* Écrans publics : ils portent déjà leur propre <title> + métas SEO. */}
        <Route path={AppRoutes.public.landing} element={native ? toHome : <LandingScreen />} />
        <Route path={AppRoutes.public.pricing} element={
          native ? <ProtectedRoute><NativeSubscriptionScreen /></ProtectedRoute> : <PricingScreen />
        } />
        <Route path={AppRoutes.public.demo} element={native ? toHome : <DemoScreen />} />
        <Route
          path={AppRoutes.public.dataVolleyAlternative}
          element={native ? toHome : <DataVolleyAlternativeScreen />}
        />
        <Route path={AppRoutes.public.dataVolleyComparison} element={native ? toHome : <DataVolleyVsScreen />} />

        <Route path={AppRoutes.dashboard.home} element={
          <PageTitle titleKey="dashboard">
            <ProtectedRoute>
              <HomeScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.match.setup} element={
          <PageTitle titleKey="match_setup">
            <ProtectedRoute roles={[Role.COACH]}>
              <MatchSetupScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.match.import} element={
          <PageTitle titleKey="match_import">
            <ProtectedRoute roles={[Role.COACH]}>
              <MatchImportScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.match.importUpload} element={
          <PageTitle titleKey="match_import_upload">
            <ProtectedRoute roles={[Role.COACH]}>
              <DvwUploadScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.match.importDraft()} element={
          <PageTitle titleKey="match_import_draft">
            <ProtectedRoute roles={[Role.COACH]}>
              <DvwImportDraftScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.match.input} element={
          <PageTitle titleKey="match_input">
            <ProtectedRoute roles={[Role.COACH]}>
              <MatchInputScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.team.list} element={
          <PageTitle titleKey="teams">
            <ProtectedRoute roles={[Role.COACH]}>
              <TeamListScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.team.detail()} element={
          <PageTitle titleKey="team_detail">
            <ProtectedRoute roles={[Role.COACH]}>
              <TeamSetupScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.gamePlans.list} element={
          <PageTitle titleKey="game_plans">
            <ProtectedRoute roles={[Role.COACH, Role.CLUB_ADMIN, Role.PLAYER]}>
              <GamePlansScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.gamePlans.view()} element={
          <PageTitle titleKey="game_plan">
            <ProtectedRoute roles={[Role.COACH, Role.CLUB_ADMIN, Role.PLAYER]}>
              <GamePlanViewScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.team.opponentDetail()} element={
          <PageTitle titleKey="opponent_detail">
            <ProtectedRoute roles={[Role.COACH, Role.CLUB_ADMIN]}>
              <OpponentTeamDetailScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route
          path={AppRoutes.scout.home}
          element={<Navigate to={AppRoutes.match.adminOthers} replace />}
        />

        <Route path={AppRoutes.profile.main} element={
          <PageTitle titleKey="profile">
            <ProtectedRoute>
              <ProfileScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.payment.success} element={
          <ProtectedRoute>
            <PaymentSuccessScreen />
          </ProtectedRoute>
        } />

        <Route path={AppRoutes.stats.withMatch()} element={
          <PageTitle titleKey="stats">
            <ProtectedRoute>
              <PlayerAccessRoute area="stats">
                <StatsScreen />
              </PlayerAccessRoute>
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.stats.main} element={
          <PageTitle titleKey="stats">
            <ProtectedRoute>
              <PlayerAccessRoute area="stats">
                <StatsScreen />
              </PlayerAccessRoute>
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.match.admin} element={
          <PageTitle titleKey="all_matches">
            <ProtectedRoute roles={[Role.COACH]}>
              <AllMatchesScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.membership.requests} element={
          <PageTitle titleKey="membership">
            <ProtectedRoute>
              <MembershipRequestsScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.cartography.main} element={
          <PageTitle titleKey="cartography">
            <ProtectedRoute roles={[Role.COACH]}>
              <CartographyScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.video.main} element={
          <PageTitle titleKey="video">
            <ProtectedRoute>
              <PlayerAccessRoute area="video">
                <VideoScreen />
              </PlayerAccessRoute>
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.matchReview.main} element={
          <PageTitle titleKey="match_review">
            <ProtectedRoute roles={[Role.COACH]}>
              <MatchReviewScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.superAdmin.dashboard} element={
          <PageTitle titleKey="super_admin">
            <ProtectedRoute roles={[Role.SUPER_ADMIN]}>
              <SuperAdminScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.faq.main} element={
          <PageTitle titleKey="faq">
            <ProtectedRoute>
              <FaqScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        {/* The website's phone tab bar leads here too (VOL-277). */}
        <Route path={AppRoutes.nativeApp.more} element={
          <ProtectedRoute><NativeMoreScreen /></ProtectedRoute>
        } />

        <Route path={AppRoutes.tutorials.main} element={
          <PageTitle titleKey="tutorials">
            <ProtectedRoute>
              <TutorialsScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.tutorials.article()} element={
          <PageTitle titleKey="tutorials">
            <ProtectedRoute>
              <TutorialArticleScreen />
            </ProtectedRoute>
          </PageTitle>
        } />

        <Route path={AppRoutes.legal.mentions} element={legal(AppRoutes.legal.mentions,
          <PageTitle titleKey="legal_mentions"><MentionsLegalesScreen /></PageTitle>,
        )} />
        <Route path={AppRoutes.legal.privacy} element={legal(AppRoutes.legal.privacy,
          <PageTitle titleKey="legal_privacy"><ConfidentialiteScreen /></PageTitle>,
        )} />
        <Route path={AppRoutes.legal.cgu} element={legal(AppRoutes.legal.cgu,
          <PageTitle titleKey="legal_cgu"><CguScreen /></PageTitle>,
        )} />
        <Route path={AppRoutes.legal.cgv} element={legal(AppRoutes.legal.cgv,
          <PageTitle titleKey="legal_cgv"><CgvScreen /></PageTitle>,
        )} />

        <Route path="*" element={native ? toHome : <Navigate to={AppRoutes.public.landing} replace />} />
      </Routes>
    </Suspense>
  );
};
